import React, { useEffect, useRef, useState } from 'react';
import './AIMarkingConsole.css';

interface LogEntry {
  message: string;
  level: 'info' | 'success' | 'warning' | 'error' | 'process' | 'system';
  timestamp: string;
}

interface AIMarkingConsoleProps {
  isVisible: boolean;
  logs?: LogEntry[];
  onClose?: () => void;
}

const AIMarkingConsole: React.FC<AIMarkingConsoleProps> = ({
  isVisible,
  logs = [],
  onClose
}) => {
  const consoleEndRef = useRef<HTMLDivElement>(null);
  const [showCursor, setShowCursor] = useState(true);
  const [isMinimized, setIsMinimized] = useState(false);

  // Auto-scroll to bottom when new logs arrive
  useEffect(() => {
    if (consoleEndRef.current) {
      consoleEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [logs]);

  // Blinking cursor effect
  useEffect(() => {
    const interval = setInterval(() => {
      setShowCursor(prev => !prev);
    }, 500);
    return () => clearInterval(interval);
  }, []);

  if (!isVisible) return null;

  return (
    <div className={`ai-marking-console ${isMinimized ? 'minimized' : ''}`}>
      <div className="console-header">
        <div>
          <span className="console-title">🖥️ AI Analysis Process</span>
          {!isMinimized && <span className="console-subtitle"> - Live Backend Status</span>}
        </div>
        <div className="console-controls">
          <button
            onClick={() => setIsMinimized(!isMinimized)}
            className="console-minimize-btn"
            title={isMinimized ? "Maximize" : "Minimize"}
          >
            {isMinimized ? '□' : '—'}
          </button>
          {onClose && (
            <button
              onClick={onClose}
              className="console-close-btn"
              title="Close console"
            >
              ✕
            </button>
          )}
        </div>
      </div>
      {!isMinimized && (
        <div className="console-body">
          {logs.map((log, index) => (
            <div key={index} className={`console-line ${log.level || 'info'}`}>
              <span className="timestamp">[{log.timestamp}]</span>
              <span className="message">{log.message}</span>
            </div>
          ))}
          {logs.length === 0 && (
            <div className="console-line info">
              <span className="message">Waiting for AI analysis to start...</span>
            </div>
          )}
          <div className="console-line current">
            <span className={`cursor ${showCursor ? 'visible' : ''}`}>▊</span>
          </div>
          <div ref={consoleEndRef} />
        </div>
      )}
    </div>
  );
};

export default AIMarkingConsole;
